/**
 * Pure helpers for the read-only admin data browser: paging maths, click-to-sort
 * toggling and cell formatting. Kept free of React so they can be unit tested.
 */

export interface AdminSortState {
  sort?: string;
  desc: boolean;
}

/** Total number of pages for `total` rows at `pageSize`, never less than one. */
export function pageCount(total: number, pageSize: number): number {
  if (!Number.isFinite(pageSize) || pageSize <= 0) return 1;
  if (!Number.isFinite(total) || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

/** Clamps a (possibly stale) page number into the range implied by `total`. */
export function clampPage(page: number, total: number, pageSize: number): number {
  if (!Number.isFinite(page) || page < 1) return 1;
  return Math.min(Math.floor(page), pageCount(total, pageSize));
}

/**
 * Server-side sort toggle: the first click on a column sorts ascending, a second
 * click flips to descending; clicking a different column restarts ascending.
 */
export function toggleSort(current: AdminSortState, column: string): AdminSortState {
  if (current.sort !== column) return { sort: column, desc: false };
  return { sort: column, desc: !current.desc };
}

/** True when the query should be sent as a UUID restaurant filter. */
export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * Renders a raw database cell: null/undefined become an em dash, scalars render
 * as-is, and jsonb objects/arrays render as compact JSON.
 */
export function formatAdminCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? '—';
  } catch {
    return String(value);
  }
}
